import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    useTheme,
    alpha,
    Button,
    Grid,
    Stack,
    Tooltip,
    Chip,
    InputAdornment,
    TextField,
    IconButton,
    Fade,
    Card,
    CardContent,
    Avatar,
    CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    Visibility,
    People,
    Business,
    MonetizationOn,
    Rocket,
    CheckCircle,
    Cancel,
    AccessTime,
    Search as SearchIcon,
    Clear as ClearIcon,
    Warning,
    TrendingUp,
    Assessment,
    AccountBalance,
    ArrowForward,
    BusinessCenter,
    Email,
    Group
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAdminStatistics, fetchServiceHealth, fetchUsers, searchUsers, fetchPendingStartups, fetchPendingCommittee, fetchAllStartups, fetchWaitingComments, approveQuestion, approveAnswer, fetchWaitingUpdates, approveUpdate, deleteQuestion, deleteAnswer } from '../store/slices/adminSlice';
import { debounce } from 'lodash';
import { getFileLink } from '../utils/getFileLink';
import PendingCorporateUsersPage from './PendingCorporateUsersPage';
import { toast } from 'react-toastify';
import { ApiHelper } from '../utils/apiHelper';

const STATUS_CONFIG = {
    PENDING: {
        label: 'Beklemede',
        color: 'default',
        next: null
    },
    APPROVED: {
        label: 'Onaylandı',
        color: 'success',
        next: null
    },
    REJECTED: {
        label: 'Reddedildi',
        color: 'error',
        next: null
    },
    WAITING_REVIEW: {
        label: 'İnceleme Bekliyor',
        color: 'warning',
        next: 'WAITING_INVESTMENT_COMMITTEE',
        nextLabel: 'Yatırım Komitesine Gönder'
    },
    WAITING_INVESTMENT_COMMITTEE: {
        label: 'Yatırım Komitesi İncelemesinde',
        color: 'info',
        next: 'WAITING_MKK_APPROVAL',
        nextLabel: 'KFS Onayına Gönder'
    },
    WAITING_MKK_APPROVAL: {
        label: 'KFS Onayı Bekliyor',
        color: 'secondary',
        next: 'WAITING_TAKASBANK_APPROVAL',
        nextLabel: 'Takasbank Onayına Gönder'
    },
    WAITING_TAKASBANK_APPROVAL: {
        label: 'Takasbank Onayı Bekliyor',
        color: 'primary',
        next: 'LAST_VIEW',
        nextLabel: 'Son İncelemeye Gönder'
    },
    LAST_VIEW: {
        label: 'Son İnceleme',
        color: 'default',
        next: 'APPROVED',
        nextLabel: 'Onaya Gönder'
    }
};

const DashboardPage = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const dispatch = useDispatch();
    const { statistics = {}, services = {}, users = {}, pendingStartups = {}, pendingCommitteeStartups = {}, waitingComments = [], loadingComments,loading, waitingUpdates = [] } = useSelector((state) => state.admin || {});
    const { adminInfo } = useSelector((state) => state.auth);

    const [preFundStartups, setPreFundStartups] = useState([]);
    const [loadingPreFund, setLoadingPreFund] = useState(false);

    // Debounced search function
    const debouncedSearch = useCallback(
        debounce((text) => {
            if (text.trim()) {
                dispatch(searchUsers(text));
            } else {
                dispatch(fetchUsers(page, rowsPerPage));
            }
        }, 500),
        [dispatch, page, rowsPerPage]
    );

    useEffect(() => {
        if (adminInfo?.type?.includes('COMMITTEE')) {
            dispatch(fetchPendingCommittee());
        } else {
            dispatch(fetchPendingStartups());
        }
        dispatch(fetchAdminStatistics());
        dispatch(fetchServiceHealth());
        dispatch(fetchWaitingComments());
        dispatch(fetchWaitingUpdates());

        console.log('waitingUpdates', waitingUpdates);
        if (!searchQuery.trim()) {
            dispatch(fetchUsers(page, rowsPerPage));
        }

        // Polling for service health every 30 seconds
        const interval = setInterval(() => {
            dispatch(fetchServiceHealth());
        }, 30000);

        return () => clearInterval(interval);
    }, [dispatch, rowsPerPage, page, searchQuery, adminInfo]);

    useEffect(() => {
        const fetchPreFundStartups = async () => {
            setLoadingPreFund(true);
            try {
                const response = await ApiHelper.get('/admin/pre-fund-startups');
                if (response.data.code === "00000") {
                    setPreFundStartups(response.data.preFundStartups || []);
                }
            } catch (error) {
                console.error('Error fetching pre-fund startups:', error);
                toast.error('Ön yatırım projeleri yüklenirken bir hata oluştu');
            } finally {
                setLoadingPreFund(false);
            }
        };

        fetchPreFundStartups();
    }, []);

    const handleApproveComment = async (item) => {
        try {
            if (item.isQuestion) {
                await dispatch(approveQuestion({
                    questionId: item.questionId,
                    startupId: item.startupId
                })).unwrap();
            } else {
                await dispatch(approveAnswer({
                    answerId: item.answerId,
                    startupId: item.startupId
                })).unwrap();
            }
            // Show success message
            toast.success('Başarıyla onaylandı');
        } catch (error) {
            toast.error('Bir hata oluştu');
        }
    };

    const handleDeleteComment = async (item) => {
        try {
            if (item.isQuestion) {
                await dispatch(deleteQuestion({
                    questionId: item.questionId,
                    startupId: item.startupId
                })).unwrap();
            } else {
                await dispatch(deleteAnswer({
                    answerId: item.answerId,
                    startupId: item.startupId
                })).unwrap();
            }
            // Refresh the comments list
            dispatch(fetchWaitingComments());
            // Show success message
            toast.success('Başarıyla silindi');
        } catch (error) {
            toast.error('Bir hata oluştu');
        }
    };

    const handleApproveUpdate = async (item) => {
        try {
            await dispatch(approveUpdate({
                updateId: item.updateId,
                startupId: item.startupId
            })).unwrap();
            dispatch(fetchWaitingUpdates());
            toast.success('Başarıyla onaylandı');
        } catch (error) {
            toast.error('Bir hata oluştu');
        }
    };

    useEffect(() => {
        console.log(users);
    }, [users]);

    const formatLargeNumber = (number) => {
        if (number >= 1000000000) {
            return (number / 1000000000).toFixed(1) + 'B';
        } else if (number >= 1000000) {
            return (number / 1000000).toFixed(1) + 'M';
        } else if (number >= 1000) {
            return (number / 1000).toFixed(1) + 'K';
        }
        return number.toString();
    };

    const statisticsCards = [
        {
            title: 'Toplam Yatırımcı',
            value: (statistics?.investorUserCount || 0).toLocaleString('tr-TR'),
            icon: <MonetizationOn sx={{ fontSize: 40, color: theme.palette.info.main }} />,
        },
        {
            title: 'Toplam Girişimci',
            value: (statistics?.entrepreneurUserCount || 0).toLocaleString('tr-TR'),
            icon: <BusinessCenter sx={{ fontSize: 40, color: theme.palette.warning.main }} />,
        },
        {
            title: 'Toplam Kampanya',
            value: (statistics?.totalStartups || 0).toLocaleString('tr-TR'),
            icon: <Business sx={{ fontSize: 40, color: theme.palette.success.main }} />,
        },
        {
            title: 'Toplam Yatırım',
            value: (statistics?.totalInvestments || 0).toLocaleString('tr-TR'),
            icon: <AccountBalance sx={{ fontSize: 40, color: theme.palette.secondary.main }} />,
        },
        {
            title: 'Toplam Hedef Fon Miktarı',
            value: `${formatLargeNumber(statistics?.totalStartupsFundingNeeded || 0)} ₺`,
            icon: <TrendingUp sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
        },
        {
            title: 'Aktif Kampanya',
            value: (statistics?.activeStartups || 0).toLocaleString('tr-TR'),
            icon: <Rocket sx={{ fontSize: 40, color: theme.palette.error.main }} />,
        }
    ];

    const getServiceStatus = (status) => {
        return status ? 'healthy' : 'unhealthy';
    };

    const servicesList = [
        {
            name: 'MKK Servisi',
            status: getServiceStatus(services.mkk?.status),
            lastCheck: 'Az önce',
            responseTime: services.mkk?.duration || '0ms'
        },
        {
            name: 'MKK Eşitleme Servisi',
            status: getServiceStatus(services.mkkSyncer?.status),
            lastCheck: 'Az önce',
            responseTime: services.mkkSyncer?.duration || '0ms'
        },
        {
            name: 'Back End Servisi',
            status: getServiceStatus(services.backend?.status),
            lastCheck: 'Az önce',
            responseTime: services.backend?.duration || '0ms'
        }
    ];

    const filteredUsers = users.list?.filter(user =>
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.userDetails?.phone?.includes(searchQuery)
    ) || [];

    const handleChangePage = (event, newPage) => {
        setPage(newPage + 1);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(1);
    };

    const handleViewDetail = (userId) => {
        navigate(`/kullanici/${userId}`);
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'healthy':
                return <CheckCircle sx={{ color: theme.palette.success.main }} />;
            case 'unhealthy':
                return <Cancel sx={{ color: theme.palette.error.main }} />;
            case 'warning':
                return <AccessTime sx={{ color: theme.palette.warning.main }} />;
            default:
                return null;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'healthy':
                return theme.palette.success.main;
            case 'unhealthy':
                return theme.palette.error.main;
            case 'warning':
                return theme.palette.warning.main;
            default:
                return theme.palette.grey[500];
        }
    };

    const getAccountTypeChip = (userDetails) => {
        const isIndividual = userDetails?.userType === 'individual';
        const color = isIndividual ? theme.palette.info.main : theme.palette.secondary.main;
        const label = isIndividual ? 'Bireysel' : 'Kurumsal';

        return (
            <Chip
                label={label}
                sx={{
                    color: color,
                    bgcolor: alpha(color, 0.15),
                    border: `1px solid ${alpha(color, 0.3)}`,
                    borderRadius: '16px',
                    '& .MuiChip-label': {
                        px: 2,
                        py: 0.5,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                    },
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        bgcolor: alpha(color, 0.2),
                        boxShadow: `0 2px 8px ${alpha(color, 0.2)}`
                    }
                }}
            />
        );
    };

    const getUserTypeChip = (userDetails) => {
        let label = 'Kullanıcı';
        let color = theme.palette.info.main;

        if (userDetails?.entrepreneur && userDetails?.investor) {
            label = 'Girişimci & Yatırımcı';
            color = theme.palette.success.main;
        } else if (userDetails?.entrepreneur) {
            label = 'Girişimci';
            color = theme.palette.warning.main;
        } else if (userDetails?.investor) {
            label = 'Yatırımcı';
            color = theme.palette.primary.main;
        }

        return (
            <Chip
                label={label}
                sx={{
                    color: color,
                    bgcolor: alpha(color, 0.15),
                    border: `1px solid ${alpha(color, 0.3)}`,
                    borderRadius: '16px',
                    '& .MuiChip-label': {
                        px: 2,
                        py: 0.5,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                    },
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        bgcolor: alpha(color, 0.2),
                        boxShadow: `0 2px 8px ${alpha(color, 0.2)}`
                    }
                }}
            />
        );
    };

    const getVerificationStatus = (user) => {
        let status = 'notSubmitted';
        if (user.userApproved) {
            status = 'verified';
        }

        const statusConfig = {
            verified: {
                label: 'Doğrulandı',
                color: theme.palette.success.main,
                bgcolor: alpha(theme.palette.success.main, 0.15),
                borderColor: alpha(theme.palette.success.main, 0.3),
                icon: <CheckCircle sx={{ fontSize: '1rem', mr: 0.5 }} />
            },
            pending: {
                label: 'E-Devlet Bekleniyor',
                color: theme.palette.warning.main,
                bgcolor: alpha(theme.palette.warning.main, 0.15),
                borderColor: alpha(theme.palette.warning.main, 0.3),
                icon: <Warning sx={{ fontSize: '1rem', mr: 0.5 }} />
            },
            notSubmitted: {
                label: 'Doğrulanmamış',
                color: theme.palette.error.main,
                bgcolor: alpha(theme.palette.error.main, 0.15),
                borderColor: alpha(theme.palette.error.main, 0.3),
                icon: <Cancel sx={{ fontSize: '1rem', mr: 0.5 }} />
            }
        };

        const config = statusConfig[status];

        return (
            <Chip
                icon={config.icon}
                label={config.label}
                sx={{
                    color: config.color,
                    bgcolor: config.bgcolor,
                    border: `1px solid ${config.borderColor}`,
                    '& .MuiChip-label': {
                        px: 1,
                        fontSize: '0.75rem',
                    },
                    '& .MuiChip-icon': {
                        color: 'inherit'
                    }
                }}
                size="small"
            />
        );
    };

    // Handle search input change
    const handleSearchChange = (event) => {
        const newValue = event.target.value;
        setSearchQuery(newValue);
        debouncedSearch(newValue);
    };

    // Clear search
    const handleClearSearch = () => {
        setSearchQuery('');
        dispatch(fetchUsers(page, rowsPerPage));
    };

    const getCommitteeRole = () => {        
        if (!adminInfo?.type?.includes('COMMITTEE')) return null;
        
        return (
            <Box
                sx={{
                    mb: 4,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="subtitle1" color="text.secondary">
                        Rolünüz:
                    </Typography>
                    <Chip
                        label={adminInfo.memberName || 'Komite Üyesi'}
                        color="primary"
                        sx={{ 
                            fontWeight: 'medium',
                            '& .MuiChip-label': {
                                px: 2,
                            }
                        }}
                    />
                </Stack>
            </Box>
        );
    };

    const renderPendingStartupsSection = () => {
        // Get and filter startups based on user type and status
        const startups = (adminInfo?.type?.includes('COMMITTEE') ? 
            pendingCommitteeStartups?.list || [] : 
            pendingStartups.list || []
        ).filter(startup => 
            !adminInfo?.type?.includes('SUPER_ADMIN') || 
            (startup.startupStatus || startup.status) !== 'PENDING'
        );

        console.log('Committee Data:', pendingCommitteeStartups); // Debug log
        console.log('Admin Info:', adminInfo); // Debug log
        console.log('Filtered Startups to display:', startups); // Debug log

        const title = adminInfo?.type?.includes('SUPER_ADMIN') ?
            'Onay Bekleyen Tüm Projeler' :
            adminInfo?.type?.includes('COMMITTEE') ?
            'Komite Onayı Bekleyen Projeler' :
            'İnceleme Bekleyen Projeler';

        const description = adminInfo?.type?.includes('SUPER_ADMIN') ?
            'Aşağıdaki projeler çeşitli aşamalarda onay bekliyor. Her projenin detayını inceleyebilir ve durumunu güncelleyebilirsiniz.' :
            adminInfo?.type?.includes('COMMITTEE') ?
            'Aşağıdaki projeler sizin komite onayınızı bekliyor. Her proje için detaylı inceleme yapmanız ve kararınızı belirtmeniz gerekiyor.' :
            'Aşağıdaki projeler incelemenizi bekliyor. Her projeyi detaylı inceleyerek bir sonraki aşamaya geçirebilirsiniz.';

        return (
            <Card sx={{ p: 3, mb: 4 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" gutterBottom>
                        {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {description}
                    </Typography>
                </Box>

                <Stack spacing={2}>
                    {startups && startups.length > 0 ? (
                        startups.map((startup) => (
                            <Card
                                key={startup.startupId}
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: alpha(theme.palette.background.default, 0.6),
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.background.default, 0.9),
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                                    },
                                    transition: 'all 0.2s ease-in-out',
                                }}
                            >
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item>
                                        <Avatar
                                            src={getFileLink(startup.startupProfile?.mainLogoPath)}
                                            alt={startup.startupProfile?.name}
                                            sx={{ width: 64, height: 64 }}
                                        />
                                    </Grid>
                                    <Grid item xs>
                                        <Typography variant="h6" gutterBottom>
                                            {startup.startupProfile?.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            {startup.startupProfile?.shortDescription}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Chip
                                                label={STATUS_CONFIG[startup.startupStatus || startup.status]?.label || 'Beklemede'}
                                                color={STATUS_CONFIG[startup.startupStatus || startup.status]?.color || 'default'}
                                                size="small"
                                                sx={{ 
                                                    minWidth: 120,
                                                    fontWeight: 'medium',
                                                }}
                                            />
                                            <Button
                                                variant="contained"
                                                onClick={() => navigate(`/startuplar/detay/${startup.startupId}`)}
                                                sx={{
                                                    bgcolor: theme.palette.primary.main,
                                                    '&:hover': {
                                                        bgcolor: theme.palette.primary.dark,
                                                    },
                                                }}
                                            >
                                                İncele
                                            </Button>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Card>
                        ))
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="text.secondary">
                                {adminInfo?.type?.includes('SUPER_ADMIN') ? 
                                    'Onay bekleyen proje bulunmuyor.' : 
                                    adminInfo?.type?.includes('COMMITTEE') ?
                                    'Komite onayı bekleyen proje bulunmuyor.' :
                                    'İnceleme bekleyen proje bulunmuyor.'}
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </Card>
        );
    };

    const renderPreFundStartupsSection = () => {
        return (
            <Card sx={{ p: 3, mb: 4 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" gutterBottom>
                        Ön Yatırım Aşamasındaki Projeler
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Aşağıdaki projeler ön yatırım aşamasında bulunuyor. Her projenin detayını inceleyebilir ve durumunu güncelleyebilirsiniz.
                    </Typography>
                </Box>

                <Stack spacing={2}>
                    {loadingPreFund ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : preFundStartups.length > 0 ? (
                        preFundStartups.map((startup) => (
                            <Card
                                key={startup.startupId}
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: alpha(theme.palette.background.default, 0.6),
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.background.default, 0.9),
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                                    },
                                    transition: 'all 0.2s ease-in-out',
                                }}
                            >
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item>
                                        <Avatar
                                            src={getFileLink(startup.startupProfile?.mainLogoPath)}
                                            alt={startup.startupProfile?.name}
                                            sx={{ width: 64, height: 64 }}
                                        />
                                    </Grid>
                                    <Grid item xs>
                                        <Typography variant="h6" gutterBottom>
                                            {startup.startupProfile?.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            {startup.startupProfile?.shortDescription}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Chip
                                                label="Ön Yatırım"
                                                color="warning"
                                                size="small"
                                                sx={{ 
                                                    minWidth: 120,
                                                    fontWeight: 'medium',
                                                }}
                                            />
                                            <Button
                                                variant="contained"
                                                onClick={() => navigate(`/startuplar/detay/${startup.startupId}`)}
                                                sx={{
                                                    bgcolor: theme.palette.primary.main,
                                                    '&:hover': {
                                                        bgcolor: theme.palette.primary.dark,
                                                    },
                                                }}
                                            >
                                                İncele
                                            </Button>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Card>
                        ))
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="text.secondary">
                                Ön yatırım aşamasında proje bulunmuyor.
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </Card>
        );
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ py: 4 }}>
                {/* Committee Role Display */}
                {getCommitteeRole()}

                {/* Action Buttons */}
                <Box sx={{ mb: 5, mt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.dark, 0.05)})`,
                                    borderRadius: 2,
                                }}
                            >
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={2}
                                    justifyContent="center"
                                    alignItems="stretch"
                                >
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={() => navigate('/startuplar')}
                                        sx={{
                                            py: 2,
                                            px: 4,
                                            flex: 1,
                                            bgcolor: theme.palette.primary.main,
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                bgcolor: theme.palette.primary.dark,
                                                transform: 'translateY(-2px)',
                                                boxShadow: theme.customShadows?.z16,
                                            },
                                        }}
                                    >
                                        <Business sx={{ fontSize: 24 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="button" display="block">
                                                Tüm Projeler
                                            </Typography>
                                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                Projeleri görüntüle ve yönet
                                            </Typography>
                                        </Box>
                                        <ArrowForward />
                                    </Button>

                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={() => navigate('/yatirimlar')}
                                        sx={{
                                            py: 2,
                                            px: 4,
                                            flex: 1,
                                            bgcolor: theme.palette.success.main,
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                bgcolor: theme.palette.success.dark,
                                                transform: 'translateY(-2px)',
                                                boxShadow: theme.customShadows?.z16,
                                            },
                                        }}
                                    >
                                        <AccountBalance sx={{ fontSize: 24 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="button" display="block">
                                                Yatırımlar
                                            </Typography>
                                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                Yatırımları görüntüle
                                            </Typography>
                                        </Box>
                                        <ArrowForward />
                                    </Button>

                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={() => navigate('/mail-yonetim')}
                                        sx={{
                                            py: 2,
                                            px: 4,
                                            flex: 1,
                                            bgcolor: theme.palette.warning.dark,
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                bgcolor: theme.palette.warning.darker,
                                                transform: 'translateY(-2px)',
                                                boxShadow: theme.customShadows?.z16,
                                            },
                                        }}
                                    >
                                        <Email sx={{ fontSize: 24 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="button" display="block">
                                                Mail Yönetim Paneli
                                            </Typography>
                                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                Özel mail gönder
                                            </Typography>
                                        </Box>
                                        <ArrowForward />
                                    </Button>

                                    {/* Keep existing admin-only buttons */}
                                    {adminInfo?.type?.includes('SUPER_ADMIN') && (
                                        <>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={() => navigate('/mail-sablonlari')}
                                            sx={{
                                                py: 2,
                                                px: 4,
                                                flex: 1,
                                                bgcolor: theme.palette.warning.main,
                                                color: 'white',
                                                display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                bgcolor: theme.palette.warning.dark,
                                                transform: 'translateY(-2px)',
                                                boxShadow: theme.customShadows?.z16,
                                            },
                                            }}
                                        >
                                            <Email sx={{ fontSize: 24 }} />
                                            Mail Şablonları
                                        </Button>

                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={() => navigate('/komite-uyeleri')}
                                            sx={{
                                                py: 2,
                                                px: 4,
                                                flex: 1,
                                                bgcolor: theme.palette.info.main,
                                                color: 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    bgcolor: theme.palette.info.dark,
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: theme.customShadows?.z16,
                                                },
                                            }}
                                        >
                                            <Group sx={{ fontSize: 24 }} />
                                            Komite Üyeleri
                                        </Button>
                                        </>
                                    )}
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>

                {/* Statistics Grid */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    {statisticsCards.map((stat, index) => (
                        <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: 2,
                                    bgcolor: 'background.paper',
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    {stat.icon}
                                </Box>
                                <Typography variant="h4" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                                    {stat.value}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {stat.title}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* Services Health Section */}
                <Card sx={{ mt: 4 }}>
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 3 }}>
                            Servis Durumları
                        </Typography>
                        <Grid container spacing={3}>
                            {servicesList.map((service) => (
                                <Grid item xs={12} sm={4} key={service.name}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 1,
                                            bgcolor: 'background.neutral',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="subtitle2">{service.name}</Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                Yanıt Süresi: {service.responseTime}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={service.status === 'healthy' ? 'Aktif' : 'Pasif'}
                                            color={service.status === 'healthy' ? 'success' : 'error'}
                                            size="small"
                                        />
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Card>

                {/* Pending Startups Section */}
                {renderPendingStartupsSection()}
                {renderPreFundStartupsSection()}

                {/* User Management Section */}
                <Card sx={{ mt: 4 }}>
                    <Box sx={{ p: 3 }}>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                            spacing={2}
                            sx={{ mb: 3 }}
                        >
                            <Typography variant="h6">
                                Kullanıcı Yönetimi {loading && '(Yükleniyor...)'}
                            </Typography>

                            <TextField
                                placeholder="Email veya telefon ile ara..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                sx={{
                                    maxWidth: { sm: '300px' },
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: theme.palette.background.paper,
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme.palette.primary.main,
                                            },
                                        },
                                        '&.Mui-focused': {
                                            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                                        },
                                    },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchQuery && (
                                        <Fade in={true}>
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={handleClearSearch}
                                                    edge="end"
                                                >
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        </Fade>
                                    ),
                                }}
                            />
                        </Stack>

                        <TableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                fontWeight: 'bold',
                                                bgcolor: alpha(theme.palette.primary.main, 0.05)
                                            }}
                                        >
                                            Email
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 'bold',
                                                bgcolor: alpha(theme.palette.primary.main, 0.05)
                                            }}
                                        >
                                            Telefon
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 'bold',
                                                bgcolor: alpha(theme.palette.primary.main, 0.05)
                                            }}
                                        >
                                            Hesap Tipi
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 'bold',
                                                bgcolor: alpha(theme.palette.primary.main, 0.05)
                                            }}
                                        >
                                            Kullanıcı Tipi
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 'bold',
                                                bgcolor: alpha(theme.palette.primary.main, 0.05)
                                            }}
                                        >
                                            Doğrulama Durumu
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 'bold',
                                                bgcolor: alpha(theme.palette.primary.main, 0.05)
                                            }}
                                        >
                                            Hesap Durumu
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{
                                                fontWeight: 'bold',
                                                bgcolor: alpha(theme.palette.primary.main, 0.05)
                                            }}
                                        >
                                            İşlemler
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">Yükleniyor...</TableCell>
                                        </TableRow>
                                    ) : users.list?.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">Kullanıcı bulunamadı</TableCell>
                                        </TableRow>
                                    ) : (
                                        users.list?.map((user) => (
                                            <TableRow
                                                key={user.userId}
                                                hover
                                                sx={{
                                                    '&:hover': {
                                                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                                                    },
                                                }}
                                            >
                                                <TableCell>{user.email}</TableCell>
                                                <TableCell>{user.userDetails?.countryCode} {user.userDetails?.phone}</TableCell>
                                                <TableCell>{getAccountTypeChip(user.userDetails)}</TableCell>
                                                <TableCell>{getUserTypeChip(user.userDetails)}</TableCell>
                                                <TableCell>{getVerificationStatus(user)}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={user.enabled ? 'Aktif' : 'Bloklu'}
                                                        color={user.enabled ? 'success' : 'error'}
                                                        size="small"
                                                        sx={{
                                                            minWidth: '80px'
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<Visibility />}
                                                        onClick={() => handleViewDetail(user.userId)}
                                                        sx={{
                                                            bgcolor: theme.palette.primary.main,
                                                            '&:hover': {
                                                                bgcolor: theme.palette.primary.dark,
                                                            },
                                                        }}
                                                    >
                                                        Detay
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={users.count || 0}
                            rowsPerPage={rowsPerPage}
                            page={page - 1}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Sayfa başına satır:"
                        />
                    </Box>
                    <PendingCorporateUsersPage />
                </Card>
                <Card sx={{ mt: 4 }}>
            <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3 }}>
                    Onay Bekleyen Yorumlar
                </Typography>
                
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Proje</TableCell>
                                <TableCell>Tip</TableCell>
                                <TableCell>İçerik</TableCell>
                                <TableCell>Tarih</TableCell>
                                <TableCell align="right">İşlemler</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loadingComments ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        Yükleniyor...
                                    </TableCell>
                                </TableRow>
                            ) : waitingComments.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        Onay bekleyen yorum bulunmuyor
                                    </TableCell>
                                </TableRow>
                            ) : (
                                waitingComments.map((item) => (
                                    <TableRow key={item.questionId || item.answerId}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar
                                                    src={getFileLink(item.startupProfile?.mainLogoPath)}
                                                    sx={{ width: 40, height: 40 }}
                                                />
                                                <Typography variant="subtitle2">
                                                    {item.startupProfile?.name}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={item.isQuestion ? 'Soru' : 'Cevap'}
                                                color={item.isQuestion ? 'primary' : 'secondary'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ maxWidth: 300, wordBreak: 'break-word' }}>
                                                {item.question || item.answer}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                        {new Date(parseInt(item.registerTime)).toLocaleString('tr-TR', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                <Button
                                                    variant="contained"
                                                    color="success"
                                                    size="small"
                                                    onClick={() => handleApproveComment(item)}
                                                >
                                                    Onayla
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    size="small"
                                                    onClick={() => handleDeleteComment(item)}
                                                >
                                                    Sil
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3 }}>
                    Onay Bekleyen Yorumlar
                </Typography>
                
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Proje</TableCell>
                                <TableCell>Tip</TableCell>
                                <TableCell>İçerik</TableCell>
                                <TableCell>Döküman</TableCell>
                                <TableCell>Tarih</TableCell>
                                <TableCell align="right">İşlemler</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loadingComments ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        Yükleniyor...
                                    </TableCell>
                                </TableRow>
                            ) : waitingUpdates.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        Onay bekleyen yorum bulunmuyor
                                    </TableCell>
                                </TableRow>
                            ) : (
                                waitingUpdates.map((item) => (
                                    <TableRow key={item.updateId}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar
                                                    src={getFileLink(item.startupProfile?.mainLogoPath)}
                                                    sx={{ width: 40, height: 40 }}
                                                />
                                                <Typography variant="subtitle2">
                                                    {item.startupProfile?.name}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={'Güncelleme'}
                                                color={'primary'}
                                                size="small"
                                            />  
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ maxWidth: 300, wordBreak: 'break-word' }}>
                                                {item.updateContent}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ maxWidth: 300, wordBreak: 'break-word' }}>
                                                <Button variant="contained" color="primary" size="small" onClick={() => {
                                                    window.open(getFileLink(item.updateDocumentPath), '_blank');
                                                }}>
                                                    Dökümanı Görüntüle
                                                </Button>
                                            </Typography>
                                        </TableCell>
                                            {/* <TableCell>
                                            <Button
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                onClick={() => handleApproveComment(item)}
                                            >
                                                Onayla
                                            </Button>
                                        </TableCell> */}
                                        <TableCell>
                                        {new Date(parseInt(item.registerTime)).toLocaleString('tr-TR', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                onClick={() => handleApproveUpdate(item)}
                                            >
                                                Onayla
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Card>
            </Box>
        </Container>
    );
};

export default DashboardPage;